import { createError } from '@common/neo.tax';

import {
  advancedFilter,
  Conditions,
  Contributor,
  FilterField,
  GET_TICKETING_USERS,
  GetTicketingUsersQuery,
  GetTicketingUsersQueryVariables,
  keywordFilter,
  TicketingUserFilter,
  TicketingUserSort,
  useCurrentCompanyId,
  useCursorPaginatedQuery,
  useSticky,
} from '@neo.tax/shared';

export type ContributorTpUser = Contributor['ticketingProviderUsers'][number];

export type TicketingUser = NonNullable<GetTicketingUsersQuery['getTicketingUsers']['edges']>[number]['node'];

interface UseTicketingUsersProps {
  filterConditions?: Conditions<FilterField<TicketingUserFilter>>;
  rdTaxCreditId: string;
  searchKeyword?: string | null;
  sorting?: Array<TicketingUserSort> | TicketingUserSort;
}

export const useTicketingUsers = ({
  filterConditions,
  rdTaxCreditId,
  searchKeyword,
  sorting,
}: UseTicketingUsersProps): {
  loading: boolean;
  ticketingUsers: TicketingUser[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
} => {
  const companyId = useCurrentCompanyId();

  const {
    data: requestData,
    error,
    hasNextPage,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetTicketingUsersQuery, GetTicketingUsersQueryVariables>(
    GET_TICKETING_USERS,
    {
      variables: {
        filter: {
          and: [keywordFilter(['name'], searchKeyword), advancedFilter<TicketingUserFilter>(filterConditions)],
        },
        sorting,
        companyId,
        rdTaxCreditId,
      },
    },
    {
      getConnection: (data) => data.getTicketingUsers,
      numElementsPerFetch: 40,
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const ticketingUsers = stickyData?.getTicketingUsers?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      ticketingUsers: ticketingUsers,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    throw createError('MissingTicketingUsers', 'No data returned from getTicketingUsers query');
  }

  return {
    loading,
    ticketingUsers,
    hasNextPage,
    onLoadMoreItems,
  };
};
